// WizardAccountSection — US-125 (account + business pickers).
//
// Extracted verbatim from CreateWizard's inline "Section 2 — account +
// business" block.

import { type Business, type LinkedinAccountRow } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Card, FieldAssembly, Pill, Select } from '@barel/ui';

export interface WizardAccountSectionProps {
  accounts: LinkedinAccountRow[];
  businesses: Business[];
  accountId: string;
  onAccountChange: (next: string) => void;
  businessId: string;
  onBusinessChange: (next: string) => void;
  hasSalesNav: boolean;
}

export function WizardAccountSection({
  accounts,
  businesses,
  accountId,
  onAccountChange,
  businessId,
  onBusinessChange,
  hasSalesNav,
}: WizardAccountSectionProps) {
  const t = useT('campaigns');
  return (
    <Card padding="lg" radius="2xl" data-testid="wizard-section-account">
      <div className="mb-3">
        <strong className="text-primary text-sm">{t('create.sectionAccount.title')}</strong>
        <p className="text-tertiary text-xs mt-1">{t('create.sectionAccount.sub')}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <FieldAssembly label={t('create.sectionAccount.accountLabel')}>
            <Select value={accountId} onValueChange={onAccountChange}>
              <Select.Trigger data-testid="wizard-account">
                <Select.Value placeholder={t('create.sectionAccount.accountLabel') as string} />
              </Select.Trigger>
              <Select.Content>
                {accounts.map((a) => (
                  <Select.Item key={a.id} value={a.id}>
                    {`${a.name}${a.salesNav ? ' · Sales Nav' : ''}`}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </FieldAssembly>
          <div className="mt-2">
            <Pill tone={hasSalesNav ? 'success' : 'neutral'} size="xs">
              {hasSalesNav
                ? t('create.sectionAccount.salesNavOn')
                : t('create.sectionAccount.salesNavOff')}
            </Pill>
          </div>
        </div>
        <div>
          <FieldAssembly
            label={t('create.sectionAccount.businessLabel')}
            hint={t('create.sectionAccount.businessHelp')}
          >
            <Select value={businessId} onValueChange={onBusinessChange}>
              <Select.Trigger data-testid="wizard-business">
                <Select.Value placeholder={t('create.sectionAccount.businessLabel') as string} />
              </Select.Trigger>
              <Select.Content>
                {businesses.map((b) => (
                  <Select.Item key={b.id} value={b.id}>
                    {b.name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          </FieldAssembly>
        </div>
      </div>
    </Card>
  );
}
